import AdException from './errors/ad.exception';
import PlayerFactory from './players/player.factory';
import * as PlayerTypes from './players/constants/player.types';
import * as AdEvent from '../core/events/ad.events';
import { logDebugging, logError } from '../log/logger';
import PlayerEvent from '../core/events/player.event';
import PrebidManager from './prebidjs/prebidmanager';
import SspWrapperItemModel from '../ssp/sspWrapperItemModel';
import TpxPlayerTracker from '../tracker/tpxplayertracker';
import AdParams from '../tracker/adparams';
import { getRealVideoHeight, getRealVideoWidth } from '../utils/playerProperties';
import { AD_TYPE } from './constants/ad.types';
import { VAST_TAG } from '../ssp/constants/sspconstants';

export default class AdManager {
  constructor(idComponent, adData, extInfo, config) {
    this.idComponent = idComponent;
    this.adData = adData;
    this.extInfo = extInfo;
    this.sspwrapper = new SspWrapperItemModel(adData);
    this.adManagerEvent = new PlayerEvent('onAdManagerEvent');
    this.TYPES = { VIDEO: 'VIDEO', DISPLAY: 'DISPLAY' };
    this.initTracker(config);
    this.config = config;
  }

  initTracker(config) {
    if (this.sspwrapper.dataTracking) {
      this.tracker = new TpxPlayerTracker('AD');
      this.adParams = new AdParams();
      this.adParams.setAdRealSize(`${getRealVideoWidth(config)}x${getRealVideoHeight(config)}`);

      this.tracker.setComonParams(this.sspwrapper.dataTracking, this.extInfo);
    } else {
      // Revisado con Rubén, los anuncios directos VastXML llevan el "t" del tracking integrado
      // No vale la pena mostrar este error, dejamos solo el log de debug para identificar los anuncios
      logDebugging('No hay dataTracking en el sspwrapper', this?.adData?.cn, this.sspwrapper);
    }
  }

  checkErrors() {
    if (this.sspwrapper.isError()) {
      this.error = new AdException('No hay datos de publicidad para reproducir', '001');
    }

    if (this.sspwrapper.type === VAST_TAG && this.config.test) {
      this.error = new AdException('No reproducimos vast_tag en campañas de test', '002');
    }
  }

  async loadPrebid() {
    if (this.isPrebid) {
      const [bidder] = this.isPrebid || [];
      if (bidder && !this.error) {
        const [bidderContent] = bidder.content;
        if (bidderContent) {
          const prebidManager = new PrebidManager(this.idComponent, bidderContent);
          const bidderResult = await prebidManager.init();
          const [bidderWinner] = bidderResult || [];
          // this.bidderVastXml = bidderWinner.vastXml;
          return bidderWinner.vastXml;
        }
        this.error = new AdException('No hay bidder.content en la respuesta del ssp', '004');
        return null;
      }
      this.error = new AdException('No hay bidder en la respuesta del ssp', '003');
      return null;
    }
    return null;
  }

  async requestAd() {
    this.type = this.TYPES.VIDEO;
    this.checkErrors();

    this.adParams?.setFormat(this.type?.toLowerCase());
    this.adParams?.setAdType(AD_TYPE.VIDEO_INSTREAM);
    this.tracker?.sendRequestDemand(this.adParams);

    return new Promise((resolve, reject) => {
      if (this.error) {
        reject(this.error);
        return;
      }
      let playerFactory;
      try {
        playerFactory = new PlayerFactory(this.idComponent, this.adData.cid).getType(
          PlayerTypes.IMA,
        );
        playerFactory.load(this.sspwrapper).then(
          (adController) => {
            if (!this.adParams) {
              logError('No hay adParams');
              logDebugging('No hay adParams', this.sspwrapper);
            }
            this.adParams?.setDeliveryCampaingId(
              adController.adsManager.getCurrentAd()?.data?.creativeAdId,
            );
            this.adParams?.setDeliveryCreativeId(
              adController.adsManager.getCurrentAd()?.data?.creativeId,
            );
            this.tracker?.sendDelivery(this.adParams);
            this.adController = adController;
            this.activateAdEvents();
            resolve(adController);
          },
          (error) => {
            reject(error);
            this.adParams = new AdParams();
            this.adParams.setErrorCode(error?.errorCode);
            this.adParams.setErrorType(error?.type);
            this.tracker.sendError(this.adParams);
            this.adData.error = error;
            logDebugging('[AdManager]', error);
          },
        );
      } catch (error) {
        this.adData.error = error;
        logDebugging('[AdManager]', error);
        reject(error);
      }
    });
  }

  requestDisplay(itemContent, adunit) {
    this.type = this.TYPES.DISPLAY;
    this.adParams?.setFormat(this.type?.toLowerCase());
    this.adParams?.setAdType(AD_TYPE.MREC);
    return new Promise((resolve, reject) => {
      if (this.error) {
        reject(this.error);
        return;
      }
      let playerFactory;
      try {
        playerFactory = new PlayerFactory(this.idComponent, this.cid, itemContent).getType(
          PlayerTypes.DISPLAY,
        );
        playerFactory.load(adunit).then(
          (adController) => {
            this.adController = adController;
            this.activateAdEvents();
            resolve(adController);
          },
          (error) => {
            reject(error);
            this.adData.error = error;
            logDebugging('[AdManager]', error);
          },
        );
      } catch (error) {
        this.adData.error = error;
        logDebugging('[AdManager]', error);
        reject(error);
      }
    });
  }

  activateAdEvents() {
    this.adController.getEvent().subscribe((sender, eventArgs) => {
      const [action, time, duration, error] = eventArgs.message;
      let showLog = true;

      switch (action) {
        case AdEvent.AdError:
          logDebugging('autoStart ', this.adController.autoStart);
          this.adData.error = error;
          this.adData.ended = true;
          if (!this.adController.autoStart) {
            this.dispatchEvent([AdEvent.AdError, this.adData]);
          }

          break;
        case AdEvent.AdLoaded:
          break;
        case AdEvent.AdStarted:
          this.adData.delivered = true;
          this.adParams?.setDeliveryVideoDuration(duration);
          this.tracker?.sendDeliveryWinner(this.adParams);
          this.tracker?.sendImpression(action, this.adParams);
          this.dispatchEvent([AdEvent.AdStarted, this.adData]);
          break;
        case AdEvent.AdStopped:
          this.adData.ended = true;

          this.tracker?.sendImpression(action, this.adParams);
          setTimeout(() => {
            this.dispatchEvent([AdEvent.AdStopped, this.adData]);
          }, 200);

          break;
        case AdEvent.AdPaused:
          this.dispatchEvent([AdEvent.AdPaused, this.adData]);
          break;
        case AdEvent.AdPlaying:
          this.dispatchEvent([AdEvent.AdPlaying, this.adData]);
          break;
        case AdEvent.AdClickThru:
          break;
        case AdEvent.AdShowSkip:
          this.adController.showSkip = true;
          this.dispatchEvent([AdEvent.AdShowSkip, this.adData]);
          break;
        case AdEvent.AdRemainingTimeChange:
          showLog = false;
          // if (time > 3 && !this.reachedPaused) {
          //   this.adController.pause();
          //   this.reachedPaused = true;
          // }
          break;
        case AdEvent.AdVideoFirstQuartile:
        case AdEvent.AdVideoMidpoint:
        case AdEvent.AdVideoThirdQuartile:
          this.tracker?.sendImpression(action, this.adParams);
          break;
        default:
          logDebugging(`AdEvent not controlled ${action}`);
          break;
      }
      if (showLog) {
        logDebugging('[AdManager]', this.adData.cid, action, time, duration);
      }
    });
  }

  getEvent() {
    return this.adManagerEvent;
  }

  isPlayingNow() {
    return this.adData.adController && this.adData.delivered && !this.adData.ended;
  }

  removeAdEvents() {
    this.adController.getEvent().unsubscribe();
  }

  dispatchEvent(arg) {
    this.adManagerEvent.fire(this, { message: arg });
  }
}
